import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Container,
  BackButton,
  Title,
  Section,
  PodiumGrid,
  PodiumName,
  LeaderboardGrid,
  LeaderboardItem,
  LeaderboardRank,
  LeaderboardName,
  EmptyStateCard,
  EmptyStateTitle,
  EmptyStateText,
  PodiumAvatarWrapper,
  PodiumAvatar,
  PodiumAvatarFallback,
  PodiumMedalBadge,
  EmptyStateIcon,
  PodiumContainer,
  PodiumUsernameWrapper,
  TemplateRow,
  TemplateColumn,
  TemplateValue,
  TemplateLabel,
  FooterText,
  TemplateFooter,
  Pagination,
  PaginationButton,
  PaginationCurrent,
  TemplatesSection,
  MobileTemplateRow,
  ModalButton,
  TitleContainer,
  LeaderboardItemWrapper,
  LeaderboardMedalBadge,
  LeaderboardMedalShimmer,
  LeaderboardMedalPopup,
  LeaderboardMedalPopupMedal,
  LeaderboardMedalPopupText,
  LeaderboardMedalPopupBold,
} from "./RewardsTemplatePage.styles";

import MainContainer from "../../components/MainContainer";
import { useNavigate } from "react-router-dom";

import { ReactComponent as StarSvg } from "../../assets/icons/star.svg";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as TrophySvg } from "../../assets/icons/rewards.svg";

import FirstPng from "../../assets/images/1.png";
import SecondPng from "../../assets/images/2.png";
import ThirdPng from "../../assets/images/3.png";

import GoldMedal from "../../assets/images/goldMedal.webp";
import SilverMedal from "../../assets/images/silverMedal.webp";

import Button from "../../components/utils/Button/Button";
import DefaultProfilePhoto from "../../components/DefaultProfilePhoto/DefaultProfilePhoto";
import {
  selectGetTopCreatorsPending,
  selectTopCreators,
} from "../../store/slices/model/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { fetchTopCreators } from "../../store/slices/model/asyncThunks";
import { selectUser } from "../../store/slices/user/slice";

import { formatTimeAgo, priceFormatter } from "../../utils/utils";
import Loader from "../../components/utils/Loader/Loader";
import CreatorsRewardModal from "../../components/CreatorsRewardModal/CreatorsRewardModal";

const RewardsTemplatePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);

  const topCreators = useSelector(selectTopCreators);
  const loading = useSelector(selectGetTopCreatorsPending);

  const [isCreatorsRewardModalOpen, setIsCreatorsRewardModalOpen] =
    useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [openMedalIndex, setOpenMedalIndex] = useState(null);
  const medalPopupRef = useRef(null);
  const itemsPerPage = 10;

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchTopCreators())
        .unwrap()
        .then()
        .catch((err) => {
          console.log(err);
        });
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (openMedalIndex === null) return;
    const handler = (e) => {
      if (medalPopupRef.current && !medalPopupRef.current.contains(e.target)) {
        setOpenMedalIndex(null);
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handler);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handler);
    };
  }, [openMedalIndex]);

  const handleBack = () => {
    navigate("/");
    localStorage.setItem("activeTabIndex", "0");

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  };

  const podiumOrder = [2, 1, 3];

  const getPaginatedTemplates = () => {
    if (!user?.approvedTrainModels) return [];

    const templatesArray = Object.entries(user.approvedTrainModels);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return templatesArray.slice(startIndex, endIndex);
  };

  const totalTemplates = user?.approvedTrainModels
    ? Object.keys(user.approvedTrainModels).length
    : 0;
  const totalPages = Math.ceil(totalTemplates / itemsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const paginatedTemplates = getPaginatedTemplates();

  const handleNavigateToUser = (username) => {
    navigate(`/profile/${username}`);
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  };

  return (
    <MainContainer hideOverflowX>
      <Container>
        <BackButton onClick={handleBack}>
          <ChevronSvg />
          Back
        </BackButton>
        <TitleContainer>
          <Title>Top Creators (Last 7 days)</Title>
          <ModalButton onClick={() => setIsCreatorsRewardModalOpen(true)}>
            🏆 Rewards
          </ModalButton>
        </TitleContainer>
        {loading ? (
          <Loader text="Loading" />
        ) : topCreators && topCreators.length > 0 ? (
          <>
            <Section>
              {/* Top 3 Podium */}
              <PodiumGrid>
                {podiumOrder.map((rank, index) => {
                  const creator = topCreators?.[rank - 1];
                  return (
                    creator && (
                      <PodiumContainer
                        key={index}
                        style={{ marginBottom: rank === 1 ? 20 : 0 }}
                      >
                        <PodiumUsernameWrapper>
                          <PodiumAvatarWrapper
                            onClick={() =>
                              handleNavigateToUser(creator.username)
                            }
                          >
                            {creator.profilePhoto ? (
                              <PodiumAvatar
                                src={creator.profilePhoto}
                                alt={`${creator.username} profile photo`}
                              />
                            ) : (
                              <PodiumAvatarFallback>
                                <DefaultProfilePhoto />
                              </PodiumAvatarFallback>
                            )}
                            <PodiumMedalBadge $rank={rank}>
                              <img
                                src={
                                  rank === 1
                                    ? FirstPng
                                    : rank === 2
                                      ? SecondPng
                                      : ThirdPng
                                }
                                alt={`Rank ${rank}`}
                              />
                            </PodiumMedalBadge>
                          </PodiumAvatarWrapper>

                          <PodiumName
                            onClick={() =>
                              handleNavigateToUser(creator.username)
                            }
                          >
                            @{creator.username}
                          </PodiumName>
                        </PodiumUsernameWrapper>
                      </PodiumContainer>
                    )
                  );
                })}
              </PodiumGrid>

              {/* Rest of Leaderboard */}
              <LeaderboardGrid>
                {topCreators.slice(3).map((creator, index) => {
                  const isGold = index < 2;
                  const medalSrc = isGold
                    ? GoldMedal
                    : index < 7
                      ? SilverMedal
                      : null;
                  const isOpen = openMedalIndex === index;
                  return (
                    <LeaderboardItemWrapper key={index + 3}>
                      <LeaderboardItem
                        onClick={() => handleNavigateToUser(creator.username)}
                      >
                        <LeaderboardRank style={{ opacity: 0.4 }}>
                          #{index + 4}
                        </LeaderboardRank>
                        {creator.profilePhoto ? (
                          <PodiumAvatar
                            style={{
                              minWidth: 26,
                              minHeight: 26,
                              maxWidth: 26,
                              maxHeight: 26,
                              borderRadius: "50%",
                            }}
                            src={creator.profilePhoto}
                            alt={`${creator.username} profile photo`}
                          />
                        ) : (
                          <PodiumAvatarFallback
                            style={{
                              minWidth: 26,
                              minHeight: 26,
                              maxWidth: 26,
                              maxHeight: 26,
                              borderRadius: "50%",
                            }}
                          >
                            <DefaultProfilePhoto size={26} />
                          </PodiumAvatarFallback>
                        )}
                        <LeaderboardName>@{creator.username}</LeaderboardName>
                        {medalSrc && (
                          <>
                            <LeaderboardMedalBadge
                              src={medalSrc}
                              alt={isGold ? "Gold Medal" : "Silver Medal"}
                              onClick={() => setOpenMedalIndex(index)}
                            />
                            <LeaderboardMedalShimmer $src={medalSrc} />
                          </>
                        )}
                      </LeaderboardItem>
                      {medalSrc && isOpen && (
                        <LeaderboardMedalPopup
                          ref={medalPopupRef}
                          onClick={() => setOpenMedalIndex(null)}
                        >
                          <LeaderboardMedalPopupMedal
                            src={medalSrc}
                            alt={isGold ? "Gold Medal" : "Silver Medal"}
                          />
                          <LeaderboardMedalShimmer $src={medalSrc} />
                          <LeaderboardMedalPopupText>
                            <LeaderboardMedalPopupBold>
                              {isGold ? "Top 5 creators" : "Top 10 creators"}
                            </LeaderboardMedalPopupBold>{" "}
                            get increased earnings.{" "}
                            {isGold ? "4-3-3-2-2-1" : "3-3-3-2-2-1"} credits by
                            month.
                          </LeaderboardMedalPopupText>
                        </LeaderboardMedalPopup>
                      )}
                    </LeaderboardItemWrapper>
                  );
                })}
              </LeaderboardGrid>
            </Section>

            {/* Published Templates Section */}
            {user?.approvedTrainModels &&
            Object.keys(user?.approvedTrainModels).length > 0 ? (
              <TemplatesSection>
                <EmptyStateCard>
                  {paginatedTemplates.map(([id, model], index) => (
                    <Fragment key={id}>
                      <TemplateRow
                        $isLast={index === paginatedTemplates.length - 1}
                      >
                        <TemplateColumn>
                          <TemplateLabel>Template</TemplateLabel>
                          <TemplateValue>{model?.name}</TemplateValue>
                        </TemplateColumn>
                        <TemplateColumn $alignItems="center">
                          <TemplateLabel>First Earned</TemplateLabel>
                          <TemplateValue>
                            {formatTimeAgo(model?.dateApproved)}
                          </TemplateValue>
                        </TemplateColumn>
                        <TemplateColumn $alignItems="flex-end">
                          <TemplateLabel>All</TemplateLabel>
                          <TemplateValue>
                            <StarSvg />
                            {priceFormatter(model?.totalEarned)}
                          </TemplateValue>
                        </TemplateColumn>
                      </TemplateRow>
                      <MobileTemplateRow
                        $isLast={index === paginatedTemplates.length - 1}
                      >
                        <TemplateColumn $gap="8px">
                          <TemplateColumn>
                            <TemplateLabel>Template</TemplateLabel>
                            <TemplateValue>{model?.name}</TemplateValue>
                          </TemplateColumn>
                          <TemplateColumn $alignItems="flex-start">
                            <TemplateLabel>First Earned</TemplateLabel>
                            <TemplateValue>
                              {formatTimeAgo(model?.dateApproved)}
                            </TemplateValue>
                          </TemplateColumn>
                        </TemplateColumn>
                        <TemplateColumn $alignItems="flex-end">
                          <TemplateLabel>All</TemplateLabel>
                          <TemplateValue>
                            <StarSvg />
                            {priceFormatter(model?.totalEarned)}
                          </TemplateValue>
                        </TemplateColumn>
                      </MobileTemplateRow>
                    </Fragment>
                  ))}
                </EmptyStateCard>

                {totalTemplates > itemsPerPage && (
                  <TemplateFooter>
                    <FooterText>
                      Showing {paginatedTemplates.length} of {totalTemplates}{" "}
                      templates
                    </FooterText>
                    <Pagination>
                      <PaginationButton
                        disabled={currentPage <= 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </PaginationButton>

                      <PaginationCurrent>
                        Page {currentPage} of {totalPages}
                      </PaginationCurrent>

                      <PaginationButton
                        disabled={currentPage >= totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </PaginationButton>
                    </Pagination>
                  </TemplateFooter>
                )}
              </TemplatesSection>
            ) : (
              <EmptyStateCard>
                <EmptyStateTitle>No Published Templates</EmptyStateTitle>
                <EmptyStateText>
                  After training, you can publish your template to earn rewards.
                </EmptyStateText>
                <Button
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      left: 0,
                      behavior: "instant",
                    });

                    navigate("/train");
                  }}
                  title={"Go to Training"}
                />
              </EmptyStateCard>
            )}
          </>
        ) : (
          // Empty State - No Top Creators
          <EmptyStateCard>
            <EmptyStateIcon>
              <TrophySvg width={48} height={48} color="white" />
            </EmptyStateIcon>
            <EmptyStateTitle>No Top Creators Yet</EmptyStateTitle>
            <EmptyStateText>
              The leaderboard is empty. Be the first to train and publish your
              template to claim the top spot!
            </EmptyStateText>
            <Button
              onClick={() => {
                navigate("/");
                localStorage.setItem("activeTabIndex", "2");
                window.scrollTo({
                  top: 0,
                  left: 0,
                  behavior: "instant",
                });
              }}
              title={"Start Training"}
            />
          </EmptyStateCard>
        )}
      </Container>
      <CreatorsRewardModal
        isOpen={isCreatorsRewardModalOpen}
        onClose={() => setIsCreatorsRewardModalOpen(false)}
      />
    </MainContainer>
  );
};

export default RewardsTemplatePage;
